/* tslint:disable */
/* eslint-disable */
/**
 * TurboClaim API
 * TurboClaim API for insurance claim management. Includes claims, documents, estimates, supplements, billing, and chat operations. Authentication is JWT-based bearer token. 
 *
 * The version of the OpenAPI document: 1.1.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { ClaimPendingInvite } from './ClaimPendingInvite';
import {
    ClaimPendingInviteFromJSON,
    ClaimPendingInviteFromJSONTyped,
    ClaimPendingInviteToJSON,
    ClaimPendingInviteToJSONTyped,
} from './ClaimPendingInvite';
import type { ClaimAssignee } from './ClaimAssignee';
import {
    ClaimAssigneeFromJSON,
    ClaimAssigneeFromJSONTyped,
    ClaimAssigneeToJSON,
    ClaimAssigneeToJSONTyped,
} from './ClaimAssignee';

/**
 * Claim plus computed aggregates for detail screens.
 * @export
 * @interface ClaimDetailed
 */
export interface ClaimDetailed {
    /**
     * 
     * @type {number}
     * @memberof ClaimDetailed
     */
    readonly id: number;
    /**
     * 
     * @type {number}
     * @memberof ClaimDetailed
     */
    companyId: number;
    /**
     * Whether the claim is archived.
     * @type {boolean}
     * @memberof ClaimDetailed
     */
    isArchived: boolean;
    /**
     * External-facing claim number
     * @type {string}
     * @memberof ClaimDetailed
     */
    claimNumber: string;
    /**
     * 
     * @type {string}
     * @memberof ClaimDetailed
     */
    policyNumber?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ClaimDetailed
     */
    insuredName: string;
    /**
     * 
     * @type {string}
     * @memberof ClaimDetailed
     */
    insuredPhone?: string | null;
    /**
     * Secondary phone number for the insured
     * @type {string}
     * @memberof ClaimDetailed
     */
    insuredPhone2?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ClaimDetailed
     */
    insuredEmail?: string | null;
    /**
     * Secondary email address for the insured
     * @type {string}
     * @memberof ClaimDetailed
     */
    insuredEmail2?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ClaimDetailed
     */
    dateOfLoss?: string | null;
    /**
     * Type of loss (free text)
     * @type {string}
     * @memberof ClaimDetailed
     */
    lossType?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ClaimDetailed
     */
    propertyAddress?: string | null;
    /**
     * Mailing address if different from property address
     * @type {string}
     * @memberof ClaimDetailed
     */
    mailingAddress?: string | null;
    /**
     * 
     * @type {number}
     * @memberof ClaimDetailed
     */
    propertySquareFootage?: number | null;
    /**
     * Insurance company name, auto-populated from insurer document metadata extraction
     * @type {string}
     * @memberof ClaimDetailed
     */
    insuranceCarrier?: string | null;
    /**
     * Internal file number for company tracking
     * @type {string}
     * @memberof ClaimDetailed
     */
    internalFileNumber?: string | null;
    /**
     * Name or company of the referring contractor (e.g. the operator who referred this claim).
     * @type {string}
     * @memberof ClaimDetailed
     */
    contractor?: string | null;
    /**
     * ID of the folder this claim is assigned to, used to group claims (e.g. by contracting company). Null if unfiled.
     * @type {number}
     * @memberof ClaimDetailed
     */
    claimFolderId?: number | null;
    /**
     * ID of the pipeline this claim flows through. The claim's status (claimStatusTemplateId) always belongs to this pipeline.
     * @type {number}
     * @memberof ClaimDetailed
     */
    pipelineId: number;
    /**
     * Resolved name of the claim's owning-company pipeline. Embedded so cross-company viewers can display the workflow without access to the owner company's pipeline list.
     * @type {string}
     * @memberof ClaimDetailed
     */
    readonly claimPipelineName?: string | null;
    /**
     * Resolved color of the claim's owning-company pipeline (hex string), embedded for the same reason as claimPipelineName.
     * @type {string}
     * @memberof ClaimDetailed
     */
    readonly claimPipelineColor?: string | null;
    /**
     * Reference to the custom status template for this claim, and the primary way to manage claim status. Normally always set — the DB column is NOT NULL and every company is guaranteed at least one template — but the response mapper is defensive and may emit null for a malformed/legacy row, so clients must tolerate null. When null, claimStatusName/claimStatusColor are also null.
     * @type {number}
     * @memberof ClaimDetailed
     */
    claimStatusTemplateId: number | null;
    /**
     * Resolved display name of the claim's status, taken from the claim's owning-company status template. Embedded so cross-company viewers (who lack access to the owner company's template list) can display the status.
     * @type {string}
     * @memberof ClaimDetailed
     */
    readonly claimStatusName?: string | null;
    /**
     * Resolved color of the claim's status template (hex string), embedded for the same reason as claimStatusName.
     * @type {string}
     * @memberof ClaimDetailed
     */
    readonly claimStatusColor?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ClaimDetailed
     */
    description?: string | null;
    /**
     * 
     * @type {number}
     * @memberof ClaimDetailed
     */
    readonly createdBy?: number;
    /**
     * 
     * @type {Date}
     * @memberof ClaimDetailed
     */
    readonly createdAt: Date;
    /**
     * 
     * @type {Date}
     * @memberof ClaimDetailed
     */
    readonly updatedAt: Date;
    /**
     * Users who have been explicitly granted access to this claim
     * @type {Array<ClaimAssignee>}
     * @memberof ClaimDetailed
     */
    readonly assignees?: Array<ClaimAssignee>;
    /**
     * Whether the authenticated user can modify this claim and related resources.
     * @type {boolean}
     * @memberof ClaimDetailed
     */
    readonly canWrite?: boolean;
    /**
     * 
     * @type {number}
     * @memberof ClaimDetailed
     */
    readonly estimateCount: number;
    /**
     * 
     * @type {number}
     * @memberof ClaimDetailed
     */
    readonly documentCount: number;
    /**
     * 
     * @type {number}
     * @memberof ClaimDetailed
     */
    readonly totalEstimatedValueCents: number;
    /**
     * 
     * @type {string}
     * @memberof ClaimDetailed
     */
    readonly createdByName?: string | null;
    /**
     * Email invites for people who have not yet created an account
     * @type {Array<ClaimPendingInvite>}
     * @memberof ClaimDetailed
     */
    readonly pendingInvites?: Array<ClaimPendingInvite>;
}

/**
 * Check if a given object implements the ClaimDetailed interface.
 */
export function instanceOfClaimDetailed(value: object): value is ClaimDetailed {
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('companyId' in value) || value['companyId'] === undefined) return false;
    if (!('isArchived' in value) || value['isArchived'] === undefined) return false;
    if (!('claimNumber' in value) || value['claimNumber'] === undefined) return false;
    if (!('insuredName' in value) || value['insuredName'] === undefined) return false;
    if (!('pipelineId' in value) || value['pipelineId'] === undefined) return false;
    if (!('claimStatusTemplateId' in value) || value['claimStatusTemplateId'] === undefined) return false;
    if (!('createdAt' in value) || value['createdAt'] === undefined) return false;
    if (!('updatedAt' in value) || value['updatedAt'] === undefined) return false;
    if (!('estimateCount' in value) || value['estimateCount'] === undefined) return false;
    if (!('documentCount' in value) || value['documentCount'] === undefined) return false;
    if (!('totalEstimatedValueCents' in value) || value['totalEstimatedValueCents'] === undefined) return false;
    return true;
}

export function ClaimDetailedFromJSON(json: any): ClaimDetailed {
    return ClaimDetailedFromJSONTyped(json, false);
}

export function ClaimDetailedFromJSONTyped(json: any, ignoreDiscriminator: boolean): ClaimDetailed {
    if (json == null) {
        return json;
    }
    return {
        
        'id': json['id'],
        'companyId': json['companyId'],
        'isArchived': json['isArchived'],
        'claimNumber': json['claimNumber'],
        'policyNumber': json['policyNumber'] == null ? undefined : json['policyNumber'],
        'insuredName': json['insuredName'],
        'insuredPhone': json['insuredPhone'] == null ? undefined : json['insuredPhone'],
        'insuredPhone2': json['insuredPhone2'] == null ? undefined : json['insuredPhone2'],
        'insuredEmail': json['insuredEmail'] == null ? undefined : json['insuredEmail'],
        'insuredEmail2': json['insuredEmail2'] == null ? undefined : json['insuredEmail2'],
        'dateOfLoss': json['dateOfLoss'] == null ? undefined : json['dateOfLoss'],
        'lossType': json['lossType'] == null ? undefined : json['lossType'],
        'propertyAddress': json['propertyAddress'] == null ? undefined : json['propertyAddress'],
        'mailingAddress': json['mailingAddress'] == null ? undefined : json['mailingAddress'],
        'propertySquareFootage': json['propertySquareFootage'] == null ? undefined : json['propertySquareFootage'],
        'insuranceCarrier': json['insuranceCarrier'] == null ? undefined : json['insuranceCarrier'],
        'internalFileNumber': json['internalFileNumber'] == null ? undefined : json['internalFileNumber'],
        'contractor': json['contractor'] == null ? undefined : json['contractor'],
        'claimFolderId': json['claimFolderId'] == null ? undefined : json['claimFolderId'],
        'pipelineId': json['pipelineId'],
        'claimPipelineName': json['claimPipelineName'] == null ? undefined : json['claimPipelineName'],
        'claimPipelineColor': json['claimPipelineColor'] == null ? undefined : json['claimPipelineColor'],
        'claimStatusTemplateId': json['claimStatusTemplateId'],
        'claimStatusName': json['claimStatusName'] == null ? undefined : json['claimStatusName'],
        'claimStatusColor': json['claimStatusColor'] == null ? undefined : json['claimStatusColor'],
        'description': json['description'] == null ? undefined : json['description'],
        'createdBy': json['createdBy'] == null ? undefined : json['createdBy'],
        'createdAt': (new Date(json['createdAt'])),
        'updatedAt': (new Date(json['updatedAt'])),
        'assignees': json['assignees'] == null ? undefined : ((json['assignees'] as Array<any>).map(ClaimAssigneeFromJSON)),
        'canWrite': json['canWrite'] == null ? undefined : json['canWrite'],
        'estimateCount': json['estimateCount'],
        'documentCount': json['documentCount'],
        'totalEstimatedValueCents': json['totalEstimatedValueCents'],
        'createdByName': json['createdByName'] == null ? undefined : json['createdByName'],
        'pendingInvites': json['pendingInvites'] == null ? undefined : ((json['pendingInvites'] as Array<any>).map(ClaimPendingInviteFromJSON)),
    };
}

export function ClaimDetailedToJSON(json: any): ClaimDetailed {
    return ClaimDetailedToJSONTyped(json, false);
}

export function ClaimDetailedToJSONTyped(value?: Omit<ClaimDetailed, 'id'|'claimPipelineName'|'claimPipelineColor'|'claimStatusName'|'claimStatusColor'|'createdBy'|'createdAt'|'updatedAt'|'assignees'|'canWrite'|'estimateCount'|'documentCount'|'totalEstimatedValueCents'|'createdByName'|'pendingInvites'> | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'companyId': value['companyId'],
        'isArchived': value['isArchived'],
        'claimNumber': value['claimNumber'],
        'policyNumber': value['policyNumber'],
        'insuredName': value['insuredName'],
        'insuredPhone': value['insuredPhone'],
        'insuredPhone2': value['insuredPhone2'],
        'insuredEmail': value['insuredEmail'],
        'insuredEmail2': value['insuredEmail2'],
        'dateOfLoss': value['dateOfLoss'],
        'lossType': value['lossType'],
        'propertyAddress': value['propertyAddress'],
        'mailingAddress': value['mailingAddress'],
        'propertySquareFootage': value['propertySquareFootage'],
        'insuranceCarrier': value['insuranceCarrier'],
        'internalFileNumber': value['internalFileNumber'],
        'contractor': value['contractor'],
        'claimFolderId': value['claimFolderId'],
        'pipelineId': value['pipelineId'],
        'claimStatusTemplateId': value['claimStatusTemplateId'],
        'description': value['description'],
    };
}

